/**
 * Magic-link callback handler. Called from main.ts BEFORE the cabinet
 * boots — when the URL is `/auth/callback?token=...`, we exchange the
 * token for a session, persist it, and rewrite the URL back to `/`
 * so a refresh doesn't re-exchange the (now-consumed) token.
 *
 * If exchange succeeds, the cabinet boots with the player already
 * signed in — the footer renders the signed-in state from the start.
 *
 * If exchange fails (token expired, network error), we render a small
 * DOM error card with a "back to cabinet" link. The cabinet does NOT
 * boot in that case — better to show a clear failure than to silently
 * land the player back on the cabinet with no indication their click
 * didn't take.
 */

import type { AuthSessionResponse } from '@arcraz/arcadia-types';
import { claimAnonymousScores } from '../api/client.js';
import { getOrCreateDeviceId } from '../runtime/identity.js';
import { AuthApiError, setSession, verifyMagicLink, verifyOAuthCallback } from '@arcraz/arcadia-auth-web';

const CALLBACK_PATH = '/auth/callback';

/** OAuth CSRF guard + which provider we started (sc-6940). */
const OAUTH_STATE_KEY = 'a84.oauth.state';
const OAUTH_PROVIDER_KEY = 'a84.oauth.provider';

export interface CallbackOutcome {
  /** True if the URL was a callback URL (whether or not the exchange
   *  succeeded). The caller uses this to decide whether the cabinet
   *  boot path should run normally. */
  handled: boolean;
  /** True if the exchange succeeded and a session was stashed. */
  signedIn: boolean;
}

/**
 * Returns immediately if the URL isn't a callback. Otherwise runs the
 * magic-link exchange and updates session state. Always rewrites the
 * URL back to `/` on completion (success OR rendered error) so a
 * subsequent reload boots the cabinet cleanly.
 */
export async function runCallbackIfNeeded(): Promise<CallbackOutcome> {
  if (window.location.pathname !== CALLBACK_PATH) {
    return { handled: false, signedIn: false };
  }

  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const code = params.get('code');
  const state = params.get('state');

  // Two inbound shapes land here: magic-link (?token) and OAuth
  // (?code&state). Neither present ⇒ malformed callback.
  if (!token && !code) {
    renderError('Sign-in link is missing its parameters. Try again.');
    return { handled: true, signedIn: false };
  }

  try {
    const resp = code
      ? await exchangeOAuth(code, state)
      : await verifyMagicLink(token as string);

    setSession({
      accessToken: resp.accessToken,
      refreshToken: resp.refreshToken,
      expiresAt: Date.now() + resp.expiresIn * 1000,
      email: resp.email,
      userId: resp.userId,
    });
    // sc-3026: claim any anonymous scores recorded against this device.
    // Fire-and-forget — don't block the cabinet boot on the round trip,
    // and don't fail the sign-in if the claim hiccups (the player can
    // re-sign in later and the operation is idempotent).
    void claimAnonymousScores({ deviceId: getOrCreateDeviceId() })
      .then((result) => {
        if (result.claimed > 0) {
          console.info('[arcadia84] claimed anonymous scores', {
            claimed: result.claimed,
          });
        }
      })
      .catch((err) => {
        console.warn('[arcadia84] claim-anonymous-scores failed', { err });
      });
    // Rewrite URL so refresh doesn't try to re-exchange.
    window.history.replaceState(null, '', '/');
    return { handled: true, signedIn: true };
  } catch (err) {
    // Restricted-env domain gate (dev): show the specific message instead of
    // the raw upstream error. Prod leaves the allowlist empty so this code
    // never comes back.
    const message =
      err instanceof AuthApiError && err.code === 'REGISTRATION_DOMAIN_RESTRICTED'
        ? 'This is a restricted environment — sign-in is limited to @arcraz.com accounts.'
        : err instanceof Error
          ? `Couldn't sign you in: ${err.message}`
          : "Couldn't sign you in: unknown error during sign-in";
    renderError(message);
    // Strip params even on failure — a refresh would otherwise re-attempt
    // with a token/code the provider already burned.
    window.history.replaceState(null, '', '/auth/callback');
    return { handled: true, signedIn: false };
  }
}

/**
 * OAuth exchange (sc-6940). Verifies the returned `state` against the
 * value we stashed at /oauth/start — a mismatch means CSRF/replay, so
 * we refuse rather than exchange. Consumes the stashed state either way.
 */
async function exchangeOAuth(code: string, state: string | null): Promise<AuthSessionResponse> {
  const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);
  const provider = sessionStorage.getItem(OAUTH_PROVIDER_KEY) ?? 'google';
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(OAUTH_PROVIDER_KEY);
  if (!state || !expectedState || state !== expectedState) {
    throw new Error('OAuth state mismatch — please try signing in again.');
  }
  return verifyOAuthCallback({ provider, code, state });
}

function renderError(message: string): void {
  // All DOM built via createElement + textContent — error message may
  // include text from a thrown SDK error, which (while unlikely to be
  // attacker-controlled in this flow) we treat as untrusted by default.
  const wrap = document.createElement('div');
  wrap.setAttribute('role', 'alertdialog');
  wrap.setAttribute('aria-labelledby', 'auth-error-title');
  wrap.style.cssText = [
    'position:fixed',
    'inset:0',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'background:rgba(4,6,13,0.85)',
    'z-index:99999',
    'font-family:ui-monospace, monospace',
    'color:#ffea00',
  ].join(';');

  const card = document.createElement('div');
  card.style.cssText = [
    'background:#0a0418',
    'border:2px solid #ffea00',
    'padding:24px 28px',
    'max-width:420px',
    'text-align:center',
  ].join(';');

  const title = document.createElement('h2');
  title.id = 'auth-error-title';
  title.textContent = 'SIGN-IN FAILED';
  title.style.cssText =
    'color:#ffea00;font-size:14px;margin:0 0 12px;letter-spacing:0.1em';

  const body = document.createElement('p');
  body.textContent = message;
  body.style.cssText =
    'color:#ffffff;font-size:11px;line-height:1.5;margin:0 0 16px';

  const link = document.createElement('a');
  link.href = '/';
  link.textContent = 'BACK TO CABINET';
  link.style.cssText = [
    'display:inline-block',
    'background:transparent',
    'border:1px solid #ffea00',
    'color:#ffea00',
    'padding:8px 18px',
    'font-size:10px',
    'letter-spacing:0.15em',
    'text-decoration:none',
  ].join(';');

  card.appendChild(title);
  card.appendChild(body);
  card.appendChild(link);
  wrap.appendChild(card);
  document.body.appendChild(wrap);
}
